import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../shared/config/transport';
import { addAuthHeaders, apiPath } from '../../shared/api';
import { Order } from '../types/transport/order';
import { ProjectMRU } from '../../shared/types/projectMRU';
import { Project } from '../types/transport/project';
import { MassType } from '../types/transport/massType';
import { NewOrder } from '../types/transport/api/newOrder';
import { Container } from '../types/transport/container';
import { Staff } from '../types/transport/staff';
import { UpdateOrder } from '../types/transport/api/updateOrder';
import { Vehicle } from '../types/transport/vehicle';
import { DateTime } from '../types/transport/dateTime';
import { Machine } from '../types/machine';
import { CreateDieselOrder } from '../types/transport/api/createDieselOrder';
import { CompleteDieselOrder } from '../types/transport/api/completeDieselOrder';
import { DieselLoad } from '../types/transport/dieselLoad';
import { DieselDeliveredAmount } from '../types/transport/dieselDeliveredAmount';
import {
  UploadedFile,
  UploadFilesResponse,
} from '../types/transport/api/uploadFilesResponse';

// Define a service using a base URL and expected endpoints
export const transportApi = createApi({
  reducerPath: 'transport',
  baseQuery: fetchBaseQuery({
    baseUrl: apiPath(config, 'transport'),
    prepareHeaders: (h) => addAuthHeaders(config, h), // Adds msal auth headers
  }),
  tagTypes: [
    'projects',
    'equipment',
    'containers',
    'assignments',
    'unavailabilities',
    'workers',
    'order',
    'orders',
    'ordersSelfDeclaration',
    'projectMRU',
    'vehicles',
    'machine',
    'diesel',
    'dieselLoad',
    'deliveredDiesel',
  ],
  endpoints: (builder) => ({
    getStaff: builder.query<Staff[], void>({
      query: () => ({
        url: '/staff',
        params: {
          activeOnly: true,
        },
      }),
    }),
    getMachines: builder.query<Machine[], void>({
      query: () => ({
        url: '/machines',
      }),
      providesTags: ['machine'],
    }),
    getMachine: builder.query<Machine, string>({
      query: (internalNumber) => ({
        url: `/machines/${internalNumber}`,
      }),
      providesTags: ['machine'],
    }),
    getUserOrder: builder.query<Order[], string>({
      query: (date) => `/me/orders?from=${date}`,
      providesTags: ['order'],
    }),
    getOrders: builder.query<
      Order[],
      { from?: DateTime; projectId?: number | string }
    >({
      query: ({ from, projectId }) => ({
        // Handle different URLs and data
        url: projectId ? `/projects/${projectId}` : '/me/orders',
        params: { from },
      }),
      providesTags: ['assignments'],
      transformResponse: (data, _, { projectId }) => {
        if (projectId) {
          return (data as Project)?.orders ?? [];
        }
        return data as Order[];
      },
    }),
    getOrdersWithSelfDeclarations: builder.query<Order[], void>({
      query: () => '/orders/self-declaration',
      providesTags: ['ordersSelfDeclaration'],
    }),
    getOrder: builder.query<Order, string | number>({
      query: (orderId) => `/orders/${orderId}`,
      providesTags: (result, error, id) => [{ type: 'order', id }],
    }),
    postOrder: builder.mutation<Order, NewOrder>({
      query: (body) => ({
        url: '/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'projectMRU',
        'containers',
      ],
    }),
    postDieselOrder: builder.mutation<Order, CreateDieselOrder>({
      query: (body) => ({
        url: '/diesel',
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'projectMRU',
        'diesel',
        'dieselLoad',
      ],
    }),
    putOrder: builder.mutation<Order, { id: string; data: UpdateOrder }>({
      query: ({ id, data }) => ({
        url: `/orders/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'containers',
      ],
    }),
    putDieselOrder: builder.mutation<Order, { id: string; data: UpdateOrder }>({
      query: ({ id, data }) => ({
        url: `/diesel/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'diesel',
        'dieselLoad',
      ],
    }),
    cancelOrder: builder.mutation<Order, number>({
      query: (id) => ({
        url: `/orders/${id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
      ],
    }),
    completeDieselOrder: builder.mutation<
      Order,
      { id: number; data: CompleteDieselOrder }
    >({
      query: ({ id, data }) => ({
        url: `/diesel/${id}/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'order', id },
        { type: 'orders', showPlanned: true },
        { type: 'orders', showPlanned: false },
        'diesel',
        { type: 'deliveredDiesel', id },
        'dieselLoad',
      ],
    }),
    getDieselLoad: builder.query<DieselLoad, void>({
      query: () => '/diesel/load',
      providesTags: ['dieselLoad'],
    }),
    getDieselDeliveredAmount: builder.query<DieselDeliveredAmount, number>({
      query: (orderId) => `/diesel/delivered-amount/${orderId}`,
      providesTags: (result, error, id) => [{ type: 'deliveredDiesel', id }],
    }),
    getProjects: builder.query<Project[], void>({
      query: () => '/projects',
    }),
    getProject: builder.query<Project, number>({
      query: (id) => `/projects/${id}`,
    }),
    getFullProject: builder.query<Project, { id: string; date: string }>({
      query: ({ id, date }) => ({
        url: `/projects/${id}`,
        params: {
          from: date,
        },
      }),
    }),
    getProjectMRUs: builder.query<ProjectMRU[], number | void>({
      query: (count) => ({
        url: '/projects/mru',
        params: count ? { count } : {},
      }),
      providesTags: ['projectMRU'],
    }),
    getMassTypes: builder.query<MassType[], void>({
      query: () => '/masstypes',
    }),
    getContainerTypes: builder.query<string[], void>({
      query: () => '/containers/types',
    }),
    getContainerWasteTypes: builder.query<string[], void>({
      query: () => '/containers/waste-types',
    }),
    getProjectContainers: builder.query<
      Container[],
      | number
      | {
          projectId: number;
          subCategory?: string;
          /** A string that isn't read - used for forcing refresh */
          random?: string;
        }
    >({
      query: (params) => {
        const { projectId, subCategory } = typeof params === 'number'
          ? { projectId: params, subCategory: undefined }
          : params;
        return {
          url: `/projects/${projectId}/containers`,
          params: subCategory ? { subCategory } : undefined,
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ['assignments'],
      transformResponse: (containers: Container[]) => containers
        .sort((a, b) => a.internalNumber.localeCompare(b.internalNumber))
        .sort(
          (a, b) => (a.orderId || a.assignmentId ? 1 : 0)
              - (b.orderId || b.assignmentId ? 1 : 0),
        ),
    }),
    getVehicles: builder.query<Vehicle[], void | DateTime>({
      query: (d) => ({
        url: '/vehicles',
        params: d ? { date: d } : undefined,
      }),
      providesTags: ['vehicles'],
    }),
    uploadFiles: builder.mutation<UploadedFile[], FileList>({
      query: (files) => {
        const formData = new FormData();
        Array.from(files).forEach((file) => {
          formData.append('files', file);
        });
        return {
          url: '/files',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: UploadFilesResponse) => response.files,
    }),
    deleteFile: builder.mutation<void, string>({
      query: (fileId) => ({
        url: `/files/${fileId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetStaffQuery,
  useGetMachineQuery,
  useGetMachinesQuery,
  useGetOrdersQuery,
  useGetUserOrderQuery,
  useGetOrderQuery,
  useGetProjectsQuery,
  useGetProjectQuery,
  useGetFullProjectQuery,
  useGetProjectMRUsQuery,
  useGetMassTypesQuery,
  useGetContainerTypesQuery,
  useGetContainerWasteTypesQuery,
  useGetProjectContainersQuery,
  useGetVehiclesQuery,
  usePostOrderMutation,
  usePutOrderMutation,
  useCancelOrderMutation,
  useCompleteDieselOrderMutation,
  usePostDieselOrderMutation,
  usePutDieselOrderMutation,
  useGetDieselLoadQuery,
  useGetDieselDeliveredAmountQuery,
  useUploadFilesMutation,
  useDeleteFileMutation,
  useGetOrdersWithSelfDeclarationsQuery,
  util,
} = transportApi;

export default transportApi.reducer;
